import {AxiosError} from 'axios';
import {useQuery, UseQueryOptions} from '@tanstack/react-query';

import {api} from '@eksab/api';

import {Competition, CompetitionType} from '@eksab/features/competition/types';
import {useAccessToken} from '@eksab/hooks';

type CompetitionFilter = 'all' | 'fantasy' | 'predictions';

export type GroupCompetitionsFilter = CompetitionFilter | 'my-competitions';

type Params = {
  filter: GroupCompetitionsFilter;
  groupId: string | undefined;
};

type QueryParams = {
  type?: Competition['type'];
  mine?: boolean;
};

const getQueryParams = ({filter}: Params): QueryParams => {
  switch (filter) {
    case 'my-competitions':
      return {mine: true};
    case 'fantasy':
      return {type: CompetitionType.FANTASY};
    case 'predictions':
      return {type: CompetitionType.PREDICTION};
    case 'all':
    default:
      return {};
  }
};

const queryKey = ({filter, groupId}: Params, isLoggedIn: boolean) => [
  'group-competitions',
  groupId,
  filter,
  isLoggedIn,
];

const queryFn = (params: Params, isLoggedIn: boolean) =>
  api
    .get(`/v2/competitions${isLoggedIn ? '/auth' : ''}?page=1&group=${params.groupId}`, {
      params: getQueryParams(params),
    })
    .then((res) => res.data.data);

export const useGroupCompetitionsQuery = (params: Params, options?: UseQueryOptions<Competition[], AxiosError>) => {
  const isLoggedIn = !!useAccessToken()?.data;
  return useQuery<Competition[], AxiosError>(queryKey(params, isLoggedIn), () => queryFn(params, isLoggedIn), {
    enabled: !!params.groupId,
    ...options,
  });
};
useGroupCompetitionsQuery.queryFn = queryFn;
useGroupCompetitionsQuery.queryKey = queryKey;
